import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type PropsWithChildren,
} from 'react';

type ClustersTableSettingsContextValue = {
  registerOpenSettings: (openSettings: () => void) => void;
  openSettings: () => void;
};

const ClustersTableSettingsContext =
  createContext<ClustersTableSettingsContextValue | null>(null);

export const ClustersTableSettingsProvider = ({
  children,
}: PropsWithChildren) => {
  const openSettingsRef = useRef<(() => void) | null>(null);

  const registerOpenSettings = useCallback((openSettings: () => void) => {
    openSettingsRef.current = openSettings;
  }, []);

  const openSettings = useCallback(() => {
    openSettingsRef.current?.();
  }, []);

  const value = useMemo(
    () => ({ registerOpenSettings, openSettings }),
    [openSettings, registerOpenSettings]
  );

  return (
    <ClustersTableSettingsContext.Provider value={value}>
      {children}
    </ClustersTableSettingsContext.Provider>
  );
};

export const useClustersTableSettings = () => {
  const context = useContext(ClustersTableSettingsContext);

  if (!context) {
    throw new Error(
      'useClustersTableSettings must be used within ClustersTableSettingsProvider'
    );
  }

  return context;
};

export const useRegisterClustersTableSettings = () =>
  useClustersTableSettings().registerOpenSettings;
